<template>
    <section class="page-item">
        <div class="row">
            <div class="col grid-margin stretch-card">
                <div class="card">
                    <div class="card-body">
                        <h4 v-if="$attrs.mode === 'new'">{{ $t('createNewTitle') }}</h4>
                        <h4 v-else class="card-title">{{ $t('editEntry') }} <strong>{{infoName}}</strong> <code>(id {{info.id}})</code></h4>
                        <p class="card-description pb-4">{{ $t('fillFieldsBelow') }}</p>
                        <div class="forms-sample">
                            <div class="row">
                                <div class="col-lg-6">
                                    <b-form-group :label="$t('forms.name')" label-for="name-input">
                                        <b-form-input type="text" id="name-input" :placeholder="$t('forms.name')" v-model="info.name" :state="nameState"/>
                                    </b-form-group>
                                </div>
                                <div class="col-lg-6">
                                    <b-form-group label="URL" label-for="url-input">
                                        <b-form-input type="text" id="url-input" placeholder="URL" v-model="info.url" :state="urlState"/>
                                    </b-form-group>
                                </div>
                            </div>

                            <b-form-group :label="$t('forms.title')" label-for="title-input" class="mb-4">
                                <div class="row align-items-center">
                                    <div class="col-auto text-right min-45px-w">{{ lang.toUpperCase() }}:</div>
                                    <div class="col"><b-form-input type="text" id="title-input" :placeholder="$t('forms.title') + ' (' + lang.toUpperCase() + ')'" v-model="info.title" :state="titleState"/></div>
                                </div>
                                <div class="row align-items-center mt-1">
                                    <div class="col-auto text-right min-45px-w">EN:</div>
                                    <div class="col"><b-form-input type="text" id="titleEn-input" :placeholder="$t('forms.title') + ' (EN)'" v-model="info.titleEn" :state="titleEnState"/></div>
                                </div>
                            </b-form-group>

                            <b-form-group :label="$t('forms.description')" label-for="description-input" class="mb-4">
                                <div class="row align-items-center">
                                    <div class="col-auto text-right min-45px-w">{{ lang.toUpperCase() }}:</div>
                                    <div class="col"><b-form-input type="text" id="description-input" :placeholder="$t('forms.description') + ' (' + lang.toUpperCase() + ')'" v-model="info.pageDescription" :state="descriptionState"/></div>
                                </div>
                                <div class="row align-items-center mt-1">
                                    <div class="col-auto text-right min-45px-w">EN:</div>
                                    <div class="col"><b-form-input type="text" id="descriptionEn-input" :placeholder="$t('forms.description') + ' (EN)'" v-model="info.pageDescriptionEn" :state="descriptionEnState"/></div>
                                </div>
                            </b-form-group>

                            <div class="row">
                                <div class="col-lg-6">
                                    <b-form-group :label="$t('forms.connectedProducts')" label-for="tags-with-dropdown" class="mb-4 mt-2">
                                        <b-form-tags id="tags-with-dropdown" v-model="connectedProductTags" no-outer-focus class="mb-2">
                                            <template v-slot="{ tags, disabled, addTag, removeTag }">

                                                <ul v-if="tags.length > 0" class="list-inline d-inline-block mb-2 w-100">
                                                    <li v-for="tag in tags" :key="tag" class="list-inline-item mb-1">
                                                        <b-form-tag
                                                            @remove="removeTag(tag)"
                                                            :title="getProductName(parseInt(tag))"
                                                            :disabled="disabled"
                                                            variant="primary"
                                                            class="large-product-tag">
                                                            {{ getProductName(parseInt(tag)) }}
                                                        </b-form-tag>
                                                    </li>
                                                </ul>

                                                <b-dropdown size="sm" variant="outline-secondary" block menu-class="w-100 scrollable-dropdown-menu">
                                                    <template #button-content>
                                                        <b-icon icon="tag-fill"></b-icon> {{ $t('forms.selectProductsHint') }}
                                                    </template>

                                                    <b-dropdown-form @submit.stop.prevent="() => {}">
                                                        <b-form-group
                                                            :label="$t('forms.search')"
                                                            label-for="tag-search-input"
                                                            label-cols-md="auto"
                                                            class="mb-0"
                                                            label-size="sm"
                                                            :description="searchDesc"
                                                            :state="true"
                                                            :disabled="disabled">
                                                            <b-form-input
                                                                v-model="productSearch"
                                                                id="tag-search-input"
                                                                type="search"
                                                                size="sm"
                                                                autocomplete="off">
                                                            </b-form-input>
                                                        </b-form-group>
                                                    </b-dropdown-form>

                                                    <b-dropdown-divider></b-dropdown-divider>

                                                    <b-dropdown-item-button
                                                        v-for="product in availableProductsOptions"
                                                        :key="product.id"
                                                        @click="onOptionClick({ option: String(product.id), addTag })">
                                                        {{ product.name }}
                                                    </b-dropdown-item-button>

                                                    <b-dropdown-text v-if="availableProductsOptions.length === 0">
                                                        {{ $t('forms.noMatchingProducts') }}
                                                    </b-dropdown-text>
                                                </b-dropdown>
                                            </template>
                                        </b-form-tags>
                                    </b-form-group>
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-lg-2">
                                    <b-form-group :label="$t('forms.author')" label-for="author-input">
                                        <b-form-input type="text" id="author-input" :placeholder="$t('forms.author')" v-model="info.author" :state="authorState"/>
                                    </b-form-group>
                                </div>
                                <div class="col-lg-10">
                                    <b-form-group :label="$t('forms.mainImage')" label-for="image-input">
                                        <b-button id="image-input" :variant="info.image && info.image.id ? 'outline-success' : 'outline-danger'" @click="openImagePicker('image-input')">
                                            {{info.image && info.image.id ? info.image.filename.substr(8) : $t('forms.select')}}
                                        </b-button>
                                        <b-popover v-if="info.image && info.image.id" target="image-input" triggers="hover" placement="right">
                                            <p class="m-0 p-0"><img :src="info.image.filename" class="browser-image"></p>
                                            <p class="m-0 p-0 text-center">{{ $t('forms.imageSize') }}: {{info.image.width}} x
                                                {{info.image.height}}</p>
                                        </b-popover>
                                    </b-form-group>
                                </div>
                            </div>


                            <b-form-group :label="$t('forms.content') + ' (' + lang.toUpperCase() + ')'" label-for="tinymce-input">
                                <editor ref="formEditor" id="tinymce-input" :api-key="editorApiKey" :init="editorConf" v-model="info.summary"/>
                            </b-form-group>

                            <b-form-group :label="$t('forms.content') + ' (EN)'" label-for="tinymceEn-input">
                                <editor ref="formEditorEn" id="tinymceEn-input" :api-key="editorApiKey" :init="editorConfEn" v-model="info.summaryEn"/>
                            </b-form-group>

                            <b-button variant="success" class="mr-2" @click="saveItem" :disabled="validated">{{ $t('forms.save') }}</b-button>
                            <b-button variant="light" v-on:click="cancel">{{ $t('forms.cancel') }}</b-button>
                            <b-button v-if="$attrs.mode === 'edit'" variant="danger" class="float-right" v-b-modal.confirm-delete>{{ $t('forms.delete') }}</b-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <confirm-delete-modal @confirm="deleteItem"/>

        <image-picker-modal @select="onImageSelected"/>
    </section>
</template>

<script>
    import axios from 'axios'
    import {Const} from '../../../config'
    import ConfirmDeleteModal from '@/components/ConfirmDeleteModal'
    import ImagePickerModal from '@/components/ImagePickerModal'
    var Editor = require('@tinymce/tinymce-vue').default;
    var app = null

    export default {
        name: 'Info',
        components: {
            'editor': Editor,
            ConfirmDeleteModal,
            ImagePickerModal
        },
        computed: {
            nameState() {
                if (this.info.name) {
                    return this.info.name.length > 0
                } else {
                    return false
                }
            },
            titleState() {
                if (this.info.title) {
                    return this.info.title.length > 0
                } else {
                    return false
                }
            },
            titleEnState() {
                if (this.info.titleEn) {
                    return this.info.titleEn.length > 0
                } else {
                    return false
                }
            },
            descriptionState() {
                if (this.info.pageDescription) {
                    return this.info.pageDescription.length > 0
                } else {
                    return false
                }
            },
            descriptionEnState() {
                if (this.info.pageDescriptionEn) {
                    return this.info.pageDescriptionEn.length > 0
                } else {
                    return false
                }
            },
            urlState() {
                if (this.info.url) {
                    return this.info.url.length > 0 &&
                        this.info.url.startsWith('/') &&
                        this.info.url.split('/').length === 2
                } else {
                    return false
                }
            },
            imageState() {
                return this.info.image && this.info.image.id > 0
            },
            authorState() {
              if (this.info.author) {
                return this.info.author.length > 0
              } else {
                return false
              }
            },
            validated() {
                return !this.nameState || !this.titleState || !this.titleEnState || !this.descriptionState ||
                    !this.descriptionEnState || !this.urlState || !this.imageState || !this.authorState
            },
            connectedProductTags: {
                get() {
                    return (this.info.connectedProductIds || []).map(id => String(id));
                },
                set(newTags) {
                    this.$set(this.info, 'connectedProductIds', newTags.map(tag => parseInt(tag, 10)));
                }
            },
            availableProductsOptions() {
                const criteria = this.productSearch.trim().toLowerCase();
                let options = this.availableProducts.filter(
                    opt => !(this.info.connectedProductIds || []).includes(opt.id)
                );

                if (criteria) {
                    options = options.filter(opt => opt.name.toLowerCase().includes(criteria));
                }

                return options;
            },
            searchDesc() {
                if (this.productSearch.trim() && this.availableProductsOptions.length === 0) {
                    return 'Brak produktów pasujących do wyszukiwania';
                }
                return '';
            }
        },
        data() {
            return {
                lang: Const.LANG,
                info: {},
                infoName: '',
                editorApiKey: Const.TINYMCE_KEY,
                editorConf: {
                    language: Const.LANG,
                    min_height: 500,
                    menubar: false,
                    convert_urls: false,
                    toolbar_sticky: true,
                    plugins: [
                        'lists link image paste wordcount code autoresize'
                    ],
                    toolbar: 'undo redo | formatselect | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link customImage | code',
                    block_formats: 'Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3; Heading 4=h4; Heading 5=h5; Heading 6=h6',
                    contextmenu: "link image imagetools table",
                    autoresize_bottom_margin: 100,
                    placeholder: this.$t('forms.writeHere'),
                    link_class_list: [
                        { title: 'Komponent Artykułu', value: 'article-link' }
                    ],
                    setup: function (editor) {
                        editor.ui.registry.addButton('customImage', {
                            icon: 'image',
                            onAction: () => app.openImagePicker('tinymce-input')
                        });
                    }
                },
                editorConfEn: {
                    language: 'en',
                    min_height: 500,
                    menubar: false,
                    convert_urls: false,
                    toolbar_sticky: true,
                    plugins: [
                        'lists link image paste wordcount code autoresize'
                    ],
                    toolbar: 'undo redo | formatselect | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link customImage | code',
                    block_formats: 'Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3; Heading 4=h4; Heading 5=h5; Heading 6=h6',
                    contextmenu: "link image imagetools table",
                    autoresize_bottom_margin: 100,
                    placeholder: this.$t('forms.writeHere'),
                    setup: function (editor) {
                        editor.ui.registry.addButton('customImage', {
                            icon: 'image',
                            onAction: () => app.openImagePicker('tinymceEn-input')
                        });
                    }
                },
                imagePickerContext: '',
                availableProducts: [],
                productSearch: ''
            }
        },
        mounted() {
            app = this
            if (this.$attrs.mode === Const.MODE_EDIT) {
                axios.get(Const.API_HOST + '/admin/info/get', {
                    params: {
                        'id': this.$route.params.id
                    }
                }).then(response => response.data).then(body => {
                    this.info = body
                    this.infoName = JSON.parse(JSON.stringify(this.info.name))
                }).catch(error => {
                    // eslint-disable-next-line no-console
                    console.error(error)
                })
            } else {
                this.info = {
                    name: '',
                    title: '',
                    titleEn: '',
                    pageDescription: '',
                    pageDescriptionEn: '',
                    image: {},
                    summary: '',
                    summaryEn: '',
                    url: '',
                    author: '',
                    connectedProductIds: []
                }
            }

            axios.get(Const.API_HOST + '/admin/productItems')
                .then(response => response.data).then(body => {
                this.availableProducts = body
            }).catch(error => {
                // eslint-disable-next-line no-console
                console.error(error)
            })
        },
        methods: {
            cancel() {
                this.$router.push('/infos')
            },
            saveItem() {
                var app = this
                axios.post(Const.API_HOST + '/admin/info/save', {
                    info: {
                        id: this.info.id,
                        name: this.info.name,
                        title: this.info.title,
                        titleEn: this.info.titleEn,
                        pageDescription: this.info.pageDescription,
                        pageDescriptionEn: this.info.pageDescriptionEn,
                        image: this.info.image,
                        summary: this.info.summary,
                        summaryEn: this.info.summaryEn,
                        url: this.info.url,
                        author: this.info.author,
                        connectedProductIds: this.info.connectedProductIds
                    }
                }).then(function (response) {
                    if (response.status === 200) {
                        app.$router.push('/infos')
                    }
                }).catch(function (error) {
                    // eslint-disable-next-line no-console
                    console.error(error)
                })
            },
            deleteItem() {
                var app = this
                axios.delete(Const.API_HOST + '/admin/info/delete', {
                    data: {
                        id: this.info.id
                    }
                }).then(function (response) {
                    if (response.status === 200) {
                        app.$router.push('/infos')
                    }
                }).catch(function (error) {
                    // eslint-disable-next-line no-console
                    console.error(error)
                })
            },
            openImagePicker(context) {
                this.imagePickerContext = context
                this.$bvModal.show('image-picker')
            },
            onImageSelected(selected) {
                let html = '<p class="par-with-images"><img src="' + selected.filename + '" alt="' + selected.alt + '" width="' + selected.width + 'px" height="' + selected.height + 'px"></p>';

                if (this.imagePickerContext === 'tinymce-input') {
                    this.$refs.formEditor.editor.execCommand('mceInsertContent', false, html);
                } else if (this.imagePickerContext === 'tinymceEn-input') {
                    this.$refs.formEditorEn.editor.execCommand('mceInsertContent', false, html);
                } else if (this.imagePickerContext === 'image-input') {
                    this.info.image = JSON.parse(JSON.stringify(selected))
                }
            },
            onOptionClick({ option, addTag }) {
                addTag(option);
                this.productSearch = '';
            },
            getProductName(id) {
                if (!this.availableProducts) return id;
                const product = this.availableProducts.find(p => p.id === id);
                return product ? product.name : id;
            }
        }
    }
</script>

<style scoped>
    .clickable /deep/ tbody td {
        cursor: pointer;
    }

    .browser-image {
        max-width: 100%;
        max-height: 100%;
        cursor: pointer;
    }

    /deep/ .tox-editor-container div:first-child {
        top: 58px !important;
    }

    .custom-multiselect-border {
      border-radius: 4px;
      padding: 0;
      border: 1px solid #28a745 !important;
    }

    ::v-deep .scrollable-dropdown-menu {
        max-height: 300px;
        overflow-y: auto;
        overflow-x: hidden;
    }

    ::v-deep .scrollable-dropdown-menu::-webkit-scrollbar {
        width: 6px;
    }
    ::v-deep .scrollable-dropdown-menu::-webkit-scrollbar-track {
        background: #f1f1f1;
    }
    ::v-deep .scrollable-dropdown-menu::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 4px;
    }
    ::v-deep .scrollable-dropdown-menu::-webkit-scrollbar-thumb:hover {
        background: #a8a8a8;
    }
    ::v-deep .large-product-tag {
        font-size: 0.8rem !important;
        padding: 0.4rem 0.6rem !important;
        font-weight: 500;
    }

    ::v-deep .large-product-tag button.b-form-tag-remove {
        font-size: 1rem;
        margin-left: 0.5rem;
    }
</style>
